/**
 * The persons API client — people, their links to entities, invitations, and
 * the self-registration queue. Spec §B (FR-B2…B4).
 *
 * A **person is not a user** (ADR-0004): they hold no platform account this app
 * knows about, they have no PAN, and they are firm-scoped. Nothing here reads
 * as identity — `portal_identity_ref` is served so a screen can say a person is
 * not reachable yet, and is **[gated: spec §9-1]** until the platform's
 * portal-identity ADR lands.
 */

import { api } from '../api'

// -- shapes ------------------------------------------------------------------

export type LinkRole = 'owner' | 'authorised' | 'viewer'

export interface PersonRecord {
  id: string
  full_name: string
  email: string | null
  mobile: string | null
  status: 'prospect' | 'active' | 'archived'
  source: 'staff' | 'self_registration'
  /** What a self-registrant *claimed* about their entity. A claim, not a
   *  record — which is why it is on the person and not on a client. */
  submitted_details: Record<string, unknown> | null
  verified_at: string | null
  notes: string | null
  /** **[gated: §9-1]** — null until portal identity is decided. Never read as
   *  permission. */
  portal_identity_ref: string | null
  created_at: string
}

export interface PersonLink {
  id: string
  client_id: string
  person_id: string
  role: LinkRole
  is_self: boolean
  status: 'active' | 'removed'
}

export interface QueueRow extends PersonRecord {
  /** The firm's own client whose PAN this prospect claimed, when there is one.
   *  A **suggestion**: staff still choose link-or-create. */
  match: { client_id: string; legal_name: string } | null
}

export interface InviteResult {
  invitation_id: string
  role: LinkRole
  status: string
  /** The only field to branch on. `false` is an ordinary outcome, not an
   *  error — the invitation was still made. */
  sent: boolean
  reason: string | null
  /** `null` when the invitation went out **without** a sign-in link — C27
   *  item 5, UAT report #10: the firm's domain is read live, and an unbound
   *  one is never named in a message. `link_omitted_reason` says why. */
  portal_host: string | null
  /** Why the sign-in link was left out of an invitation that was sent, or
   *  `null`/absent when it was not: `LINK_OMITTED_DOMAIN_NOT_LIVE` or
   *  `LINK_OMITTED_NO_DOMAIN`. */
  link_omitted_reason?: string | null
}

/**
 * The service's words for *the link was left out* (C27 item 5): the firm has a
 * domain the platform is not serving yet, or has none at all. **One constant
 * each** — `inviteOutcome` below and both invite screens branch on them, so a
 * rename on the wire is one line here.
 */
export const LINK_OMITTED_DOMAIN_NOT_LIVE = 'domain_not_live'
export const LINK_OMITTED_NO_DOMAIN = 'no_domain'

export interface VerifyResult {
  person: PersonRecord
  client_id: string
  created_client: boolean
  role: LinkRole
}

// -- the client --------------------------------------------------------------

export const persons = {
  list: (params: { search?: string; status?: string; limit?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<PersonRecord[]>(`/api/v1/persons${suffix}`)
  },
  get: (id: string) => api<PersonRecord & { links: PersonLink[] }>(`/api/v1/persons/${id}`),
  queue: () => api<QueueRow[]>('/api/v1/persons/queue'),
  create: (body: { full_name: string; email?: string; mobile?: string; notes?: string }) =>
    api<PersonRecord>('/api/v1/persons', { method: 'POST', body }),
  update: (id: string, body: Partial<{ full_name: string; email: string; mobile: string }>) =>
    api<PersonRecord>(`/api/v1/persons/${id}`, { method: 'PATCH', body }),

  link: (id: string, body: { client_id: string; role: LinkRole }) =>
    api<PersonLink>(`/api/v1/persons/${id}/links`, { method: 'POST', body }),
  setRole: (id: string, clientId: string, role: LinkRole) =>
    api<PersonLink>(`/api/v1/persons/${id}/links/${clientId}`, {
      method: 'PATCH',
      body: { role },
    }),
  unlink: (id: string, clientId: string) =>
    api<PersonLink>(`/api/v1/persons/${id}/links/${clientId}`, { method: 'DELETE' }),

  invite: (id: string, body: { client_id: string; role: LinkRole }) =>
    api<InviteResult>(`/api/v1/persons/${id}/invite`, { method: 'POST', body }),

  verify: (
    id: string,
    body: {
      client_id?: string
      entity?: {
        legal_name?: string
        kind: string
        constitution?: string | null
        company_type?: string | null
        pan?: string | null
      }
      role?: LinkRole
    },
  ) => api<VerifyResult>(`/api/v1/persons/${id}/verify`, { method: 'POST', body }),
}

// -- pure helpers, so the screens' rules are testable ------------------------

/** The three roles, in the order they narrow: owner, then authorised, then
 *  viewer. Exported so a role arriving from the wire can be checked rather
 *  than cast. */
export const LINK_ROLES: LinkRole[] = ['owner', 'authorised', 'viewer']

export const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  authorised: 'Authorised',
  viewer: 'Viewer',
}

export const ROLE_NOTES: Record<string, string> = {
  owner: 'The entity itself, or a partner or director of it.',
  authorised: 'Acts for the entity — a relative, an accountant, a manager.',
  viewer: 'Reads, and changes nothing.',
}

/**
 * Which roles a person may be given on this entity, given who is already on it.
 *
 * **ADR-0015 §2 in the browser, matching the service exactly.** An individual
 * entity has exactly one owner and it is the individual — so once that link
 * exists, `owner` is not on offer. Showing it and letting the API refuse would
 * teach staff the product is broken; hiding it without the API refusing would
 * be a UI-only rule (ADR-0029 forbids either half alone).
 */
export function roleOptions(
  kind: string,
  existing: Array<{ role: string }>,
): [LinkRole, ...LinkRole[]] {
  // A **non-empty** tuple, and the type says so: there is always at least one
  // role a person may hold on an entity, so a caller defaulting a form to
  // `options[0]` is not defaulting to `undefined`. Widening this to `LinkRole[]`
  // makes every such caller add an assertion, and an assertion is where a real
  // empty list would get through.
  if (kind !== 'individual') return ['owner', 'authorised', 'viewer']
  return existing.some((link) => link.role === 'owner')
    ? ['authorised', 'viewer']
    : ['owner', 'authorised', 'viewer']
}

/** Whether this link may be removed. An individual cannot be unlinked from
 *  their own record, and a business's last owner cannot be removed. */
export function canUnlink(
  link: { role: string; is_self: boolean },
  siblings: Array<{ role: string }>,
): boolean {
  if (link.is_self) return false
  if (link.role !== 'owner') return true
  return siblings.filter((other) => other.role === 'owner').length > 1
}

/**
 * What to tell a staff member when the invitation was made but the message was
 * not delivered.
 *
 * Each reason is a **different next action**, which is the whole point of not
 * collapsing them into "could not send": one is fixed by adding an address, one
 * by finishing the domain step, and one by waiting.
 */
export function inviteFailureCopy(reason: string | null): string {
  switch (reason) {
    case 'no_channel':
      return 'They have no email address or mobile number, so there was nowhere to send it. Add one and invite again.'
    case 'no_portal_host':
      return 'Your firm has no domain yet, so the invitation has no address to point at. Add one on the Settings page — the person is already linked to this client.'
    case 'not_delivered':
      return 'The message did not go out. The person is linked to this client either way, and you can send the invitation again.'
    default:
      return 'The invitation was recorded, but nothing was sent.'
  }
}

/** What staff are told when an invitation was sent without a sign-in link
 *  (C27 item 5). The next action is to wait for the domain, then resend. */
export const LINK_OMITTED_COPY =
  'Invitation sent without a sign-in link — your website’s domain isn’t live yet. Resend it once the domain is connected.'
export const LINK_OMITTED_NO_DOMAIN_COPY =
  'Invitation sent without a sign-in link — your website has no domain yet. Connect one under Your website → Domain, then resend it.'

/**
 * What to tell staff after an invite, in one place for the People tab and the
 * Link dialog alike: sent, sent **without the link** (C27 item 5), or made and
 * not delivered (`inviteFailureCopy`). The second is `info`, not `success` —
 * the person cannot sign in from that message, and the firm has a step to do.
 */
export function inviteOutcome(
  result: Pick<InviteResult, 'sent' | 'reason' | 'link_omitted_reason'>,
): { text: string; tone: 'success' | 'info' } {
  if (!result.sent) return { text: inviteFailureCopy(result.reason), tone: 'info' }
  if (result.link_omitted_reason === LINK_OMITTED_DOMAIN_NOT_LIVE) {
    return { text: LINK_OMITTED_COPY, tone: 'info' }
  }
  if (result.link_omitted_reason === LINK_OMITTED_NO_DOMAIN) {
    return { text: LINK_OMITTED_NO_DOMAIN_COPY, tone: 'info' }
  }
  if (result.link_omitted_reason) {
    return {
      text: 'Invitation sent without a sign-in link. Resend it once your website is live.',
      tone: 'info',
    }
  }
  return { text: 'Invitation sent.', tone: 'success' }
}

/** One line under a queue row: what this prospect claimed, in a sentence. */
export function claimSummary(submitted: Record<string, unknown> | null): string | null {
  if (!submitted) return null
  const parts: string[] = []
  if (submitted.legal_name) parts.push(String(submitted.legal_name))
  if (submitted.kind) parts.push(String(submitted.kind))
  if (submitted.pan) parts.push(`PAN ${String(submitted.pan)}`)
  // A PAN the service could not read is kept rather than dropped, and it is
  // worth showing as *unreadable* rather than as absent: a person can see it is
  // a typo, and nothing else can.
  if (submitted.pan_unreadable) parts.push(`PAN “${String(submitted.pan_unreadable)}” — unreadable`)
  return parts.length ? parts.join(' · ') : null
}
